// AI Service for clinical documentation
// Uses Web Speech API for transcription and Google Gemini for SOAP note generation

export interface AICodeSuggestion {
  code: string;
  description: string;
  type: 'ICD-10' | 'CPT';
}

export interface SOAPNote {
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  fullText: string;
}

export interface ProcessingResult {
  transcript: string;
  soapNote: SOAPNote;
  codes: AICodeSuggestion[];
}

export interface AIServiceError {
  message: string;
  code: 'MISSING_API_KEY' | 'TRANSCRIPTION_FAILED' | 'SOAP_GENERATION_FAILED' | 'NETWORK_ERROR';
}

const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent';

function getAPIKey(): string {
  const key = import.meta.env['VITE_GEMINI_API_KEY'] as string | undefined;
  if (!key) {
    throw { message: 'Gemini API key not configured. Set VITE_GEMINI_API_KEY in your environment.', code: 'MISSING_API_KEY' as const };
  }
  return key;
}

/**
 * Transcribe audio using browser Web Speech API
 */
export async function transcribeAudioWebSpeech(audioBlob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    // Create audio URL from blob
    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);

    // Check if SpeechRecognition is available
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      reject({ message: 'Speech recognition not supported in this browser', code: 'TRANSCRIPTION_FAILED' as const });
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    let finalTranscript = '';
    let isFirstResult = true;

    recognition.onresult = (event: any) => {
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }
      if (isFirstResult && finalTranscript) {
        isFirstResult = false;
      }
    };

    recognition.onerror = (event: any) => {
      URL.revokeObjectURL(audioUrl);
      reject({ message: `Speech recognition error: ${event.error}`, code: 'TRANSCRIPTION_FAILED' as const });
    };

    recognition.onend = () => {
      URL.revokeObjectURL(audioUrl);
      resolve(finalTranscript.trim());
    };

    // Start recognition - will only work if audio playback is allowed
    // For better results with recorded audio, use a speech-to-text API
    // For now, return a message indicating Web Speech API limitation
    recognition.start();

    // Timeout fallback for Web Speech API limitation with blob audio
    setTimeout(() => {
      recognition.stop();
      // If no speech detected, use a placeholder
      if (!finalTranscript.trim()) {
        // Return empty - actual implementation would need server-side transcription
        resolve('');
      }
    }, 5000);
  });
}

async function generateSOAPNoteInternal(transcript: string, apiKey: string, patientContext?: string): Promise<SOAPNote> {
  const contextSection = patientContext
    ? `\n\nPatient Context:\n${patientContext}`
    : '';

  const prompt = `You are a medical documentation assistant for healthcare providers. Generate a structured SOAP note from the provided consultation transcript.

SOAP Note Structure:
- S (Subjective): Patient's reported symptoms, history, and complaints
- O (Objective): Observable/measurable findings, examination results
- A (Assessment): Diagnosis or clinical impression
- P (Plan): Treatment plan, medications, follow-up

Important:
- Only include information explicitly stated in the transcript
- Use professional medical terminology
- Be concise but comprehensive
- Extract potential ICD-10 and CPT codes when mentioned or clearly implied

After the SOAP note, list any relevant ICD-10 and CPT codes in this format:
ICD-10: CODE - Description
CPT: CODE - Description

If no codes are clearly applicable, state "No specific codes identified."

Return the response as valid JSON with this exact structure:
{
  "subjective": "...",
  "objective": "...",
  "assessment": "...",
  "plan": "...",
  "fullText": "S (Subjective): ...",
  "codes": [
    { "code": "CODE", "description": "Description", "type": "ICD-10" },
    { "code": "CODE", "description": "Description", "type": "CPT" }
  ]
}`;

  const response = await fetch(`${GEMINI_API_URL}?key=${apiKey}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      contents: [{
        parts: [{
          text: `${prompt}\n\nConsultation Transcript:${contextSection}\n\n${transcript}`
        }]
      }],
      generationConfig: {
        temperature: 0.3,
        maxOutputTokens: 2048,
      },
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw { message: `SOAP generation failed: ${errorText}`, code: 'SOAP_GENERATION_FAILED' as const };
  }

  const data = await response.json();
  const content = data.candidates?.[0]?.content?.parts?.[0]?.text;

  if (!content) {
    throw { message: 'Empty response from Gemini', code: 'SOAP_GENERATION_FAILED' as const };
  }

  // Parse JSON from the response
  try {
    // Try to extract JSON from markdown code blocks if present
    const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)\s*```/) || content.match(/\{[\s\S]*\}/);
    const jsonString = jsonMatch ? (jsonMatch[1] || jsonMatch[0]) : content;

    const parsed = JSON.parse(jsonString);

    return {
      subjective: parsed.subjective || '',
      objective: parsed.objective || '',
      assessment: parsed.assessment || '',
      plan: parsed.plan || '',
      fullText: parsed.fullText || `S (Subjective): ${parsed.subjective || ''}\n\nO (Objective): ${parsed.objective || ''}\n\nA (Assessment): ${parsed.assessment || ''}\n\nP (Plan): ${parsed.plan || ''}`,
    };
  } catch {
    // If JSON parsing fails, create SOAP note from raw text
    const lines = content.split('\n');
    const soapNote: SOAPNote = {
      subjective: '',
      objective: '',
      assessment: '',
      plan: '',
      fullText: content,
    };

    let currentSection = '';
    for (const line of lines) {
      const upperLine = line.toUpperCase();
      if (upperLine.includes('SUBJECTIVE') || upperLine.includes('S (')) {
        currentSection = 'subjective';
      } else if (upperLine.includes('OBJECTIVE') || upperLine.includes('O (')) {
        currentSection = 'objective';
      } else if (upperLine.includes('ASSESSMENT') || upperLine.includes('A (')) {
        currentSection = 'assessment';
      } else if (upperLine.includes('PLAN') || upperLine.includes('P (')) {
        currentSection = 'plan';
      } else if (currentSection && line.trim()) {
        if (currentSection === 'subjective') {
          soapNote.subjective += (soapNote.subjective ? '\n' : '') + line;
        } else if (currentSection === 'objective') {
          soapNote.objective += (soapNote.objective ? '\n' : '') + line;
        } else if (currentSection === 'assessment') {
          soapNote.assessment += (soapNote.assessment ? '\n' : '') + line;
        } else if (currentSection === 'plan') {
          soapNote.plan += (soapNote.plan ? '\n' : '') + line;
        }
      }
    }

    return soapNote;
  }
}

function extractCodesFromResponse(content: string): AICodeSuggestion[] {
  const codes: AICodeSuggestion[] = [];

  // Match ICD-10 codes (format: LETTER + number, e.g., K04.0, J01.90)
  const icd10Regex = /ICD-?10:?\s*([A-Z]\d{2}\.?\d{0,2})\s*[-–]?\s*([^/\n]+)/gi;
  let match;
  while ((match = icd10Regex.exec(content)) !== null) {
    const codeValue = match[1];
    const description = match[2];
    if (codeValue && description) {
      codes.push({
        code: codeValue.toUpperCase(),
        description: description.trim(),
        type: 'ICD-10',
      });
    }
  }

  // Match CPT codes (5-digit codes, e.g., 99213, 99214)
  const cptRegex = /CPT:?\s*(\d{5})\s*[-–]?\s*([^/\n]+)/gi;
  while ((match = cptRegex.exec(content)) !== null) {
    const codeValue = match[1];
    const description = match[2];
    if (codeValue && description) {
      codes.push({
        code: codeValue,
        description: description.trim(),
        type: 'CPT',
      });
    }
  }

  // Remove duplicates
  const uniqueCodes = codes.filter((code, index, self) =>
    index === self.findIndex((c) => c.code === code.code && c.type === code.type)
  );

  return uniqueCodes;
}

class AIService {
  /**
   * Transcribe audio using browser Web Speech API
   * Note: For production, consider using Google Cloud Speech-to-Text API
   */
  async transcribeAudio(audioBlob: Blob): Promise<string> {
    // Use browser's built-in speech recognition as fallback
    // For better accuracy with medical terminology, consider using Google Cloud Speech-to-Text
    return transcribeAudioWebSpeech(audioBlob);
  }

  /**
   * Generate SOAP note from transcript using Gemini
   */
  async generateSOAPNote(transcript: string, patientContext?: string): Promise<SOAPNote> {
    const apiKey = getAPIKey();
    return generateSOAPNoteInternal(transcript, apiKey, patientContext);
  }

  /**
   * Suggest ICD-10 and CPT codes based on SOAP note content
   */
  async suggestCodes(soapNote: SOAPNote): Promise<AICodeSuggestion[]> {
    const apiKey = getAPIKey();

    const prompt = `You are a medical coding assistant. Based on the following SOAP note, identify and list relevant ICD-10 and CPT codes.

Return only the codes in this exact JSON format:
{
  "codes": [
    { "code": "CODE", "description": "Description", "type": "ICD-10" },
    { "code": "CODE", "description": "Description", "type": "CPT" }
  ]
}

Only include codes that are clearly supported by the documentation. If no specific codes apply, return an empty codes array.`;

    const response = await fetch(`${GEMINI_API_URL}?key=${apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{
          parts: [{ text: `${prompt}\n\nSOAP Note:\n${soapNote.fullText}` }]
        }],
        generationConfig: {
          temperature: 0.2,
          maxOutputTokens: 1024,
        },
      }),
    });

    if (!response.ok) {
      throw { message: 'Code suggestion failed', code: 'SOAP_GENERATION_FAILED' as const };
    }

    const data = await response.json();
    const content = data.candidates?.[0]?.content?.parts?.[0]?.text;

    if (!content) {
      return [];
    }

    try {
      // Try to extract JSON
      const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)\s*```/) || content.match(/\{[\s\S]*\}/);
      const jsonString = jsonMatch ? (jsonMatch[1] || jsonMatch[0]) : content;
      const parsed = JSON.parse(jsonString);
      return parsed.codes || [];
    } catch {
      // Fallback to regex extraction
      return extractCodesFromResponse(content);
    }
  }

  /**
   * Full pipeline: audio → transcript → SOAP note → codes
   */
  async processConsultation(audioBlob: Blob, patientContext?: string): Promise<ProcessingResult> {
    // Step 1: Transcribe audio (using Web Speech API)
    const transcript = await this.transcribeAudio(audioBlob);

    // Step 2: Generate SOAP note using Gemini
    const soapNote = await this.generateSOAPNote(transcript, patientContext);

    // Step 3: Extract codes based on the SOAP note
    const codes = await this.suggestCodes(soapNote);

    return {
      transcript,
      soapNote,
      codes,
    };
  }
}

// Export singleton instance
export const aiService = new AIService();
